
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useKeycloakContext } from '@/contexts/KeycloakContext';
import { useUserRole } from '@/hooks/useUserRole';
import { User, Mail, Shield, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { usePreloadHome } from '@/hooks/usePreloadHome';
import { LogoutLoading } from '@/components/ui/loading-overlay';

const Profile = () => {
  const { user, signOut, isLoading: authLoading } = useKeycloakContext();
  const { role, isLoading } = useUserRole();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  // 预加载首页资源
  usePreloadHome();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      
      // 短暂延迟以显示加载状态
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // 预先导航到首页以减少感知延迟
      navigate('/');
      
      // 执行登出操作
      await signOut();
      
    } catch (error) {
      console.error('Logout failed:', error);
      setIsSigningOut(false);
      
      // 显示错误提示
      toast({
        title: "登出失败",
        description: "退出登录时发生错误，请重试。",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <LogoutLoading isVisible={isSigningOut} />
      <div className="container max-w-4xl mx-auto py-8 px-4 sm:px-6">
      <h1 className="text-3xl font-bold mb-8">My Profile</h1>
      
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="mr-2" size={20} />
            User Information
          </CardTitle>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center">
            <Mail className="mr-2 text-muted-foreground" size={16} />
            <span className="text-sm font-medium">Email:</span>
            <span className="ml-2">{user?.email}</span>
          </div>
          <div className="flex items-center">
            <Shield className="mr-2 text-muted-foreground" size={16} />
            <span className="text-sm font-medium">Role:</span>
            <span className="ml-2 capitalize">{role}</span>
          </div>
          {user?.picture && (
            <div className="mt-4">
              <img 
                src={user.picture} 
                alt="Profile" 
                className="w-20 h-20 rounded-full"
              />
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between flex-col sm:flex-row gap-2">
          <Button
            onClick={handleSignOut}
            variant="destructive"
            disabled={isSigningOut || authLoading}
            className="transition-all duration-200 hover:scale-105"
          >
            {isSigningOut ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                <span className="animate-pulse">正在登出...</span>
              </>
            ) : (
              <>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </>
            )}
          </Button>
          {role === 'admin' && (
            <Button asChild>
              <Link to="/admin">Go to Admin Dashboard</Link>
            </Button>
          )}
        </CardFooter>
      </Card>
      </div>
    </>
  );
};

export default Profile;
